import { useState, useCallback } from 'react';

export interface DialogState<T = any> {
  isOpen: boolean;
  data: T | null;
}

/**
 * Custom hook for managing dialog state (open/closed + associated data)
 * Provides type-safe dialog management with open/close actions
 * 
 * @param initialState - Optional initial dialog state
 * @returns Object with state and action functions
 * 
 * @example
 * const viewDialog = useDialogState<CorpPolicy>();
 * viewDialog.open(policyData); // Opens dialog with policy data
 * viewDialog.close(); // Closes dialog and clears data
 * {viewDialog.state.isOpen && <Dialog data={viewDialog.state.data} />}
 */
export const useDialogState = <T = any>(
  initialState: DialogState<T> = { isOpen: false, data: null }
) => {
  const [state, setState] = useState<DialogState<T>>(initialState);
  
  const open = useCallback((data?: T) => {
    setState({ isOpen: true, data: data || null });
  }, []);
  
  const close = useCallback(() => {
    setState({ isOpen: false, data: null });
  }, []);
  
  const updateData = useCallback((data: T) => {
    setState(prev => ({ ...prev, data }));
  }, []);
  
  return {
    open,
    close,
    updateData,
    isOpen: state.isOpen,
    data: state.data,
  };
};
